import { themes } from "./themes.js";
import { safeConsole } from "./functions/safe/safeConsole.js";
import { safeGroup } from "./functions/safe/safeGroup.js";
import { safeGroupEnd } from "./functions/safe/safeGroupEnd.js";
import { formatWithColor } from "./functions/helpers/formatMessage/index.js";
import { calculateElapsedMessage } from "./functions/helpers/timing/calculateElapsedMessage.js";
import { warnIfSlow } from "./functions/helpers/timing/warnIfSlow.js";
import { outputExitMessage } from "./functions/helpers/formatting/outputExitMessage.js";
import { calculateGroupIndent } from "./functions/helpers/formatting/calculateGroupIndent.js";
import { buildEnterMessageWithPrefix } from "./functions/helpers/formatting/buildEnterMessageWithPrefix.js";
import { buildExitMessageWithPrefix } from "./functions/helpers/formatting/buildExitMessageWithPrefix.js";
import { buildStructuredLoggerMessage } from "./functions/helpers/formatting/buildStructuredLoggerMessage.js";
import { unwindEnterStack } from "./functions/tracking/unwindEnterStack.js";
/**
 * Log level hierarchy ordered from least to most verbose.
 */
const LOG_LEVEL_HIERARCHY = [
    "off",
    "fatal",
    "error",
    "warn",
    "log",
    "info",
    "debug",
    "verbose",
    "trace",
];
/**
 * Checks if a message at the given level should be logged.
 * Pure function with no side effects.
 *
 * @param messageLevel - Level to check
 * @param currentLevel - Current logger level
 * @returns True if message should be output
 */
function shouldLog(messageLevel, currentLevel) {
    const currentIndex = LOG_LEVEL_HIERARCHY.indexOf(currentLevel);
    const messageIndex = LOG_LEVEL_HIERARCHY.indexOf(messageLevel);
    return messageIndex <= currentIndex;
}
/**
 * Applies theme styling and outputs via console.
 * Orchestrates formatting and safe console output.
 * Applies indentation in text mode based on group depth.
 *
 * @param level - Log level
 * @param theme - Current theme
 * @param groupMode - Current grouping mode
 * @param depth - Current group nesting depth
 * @param message - Message to output
 * @param optionalParams - Additional parameters
 */
function outputWithTheme(level, theme, groupMode, depth, message, name, showName, ...optionalParams) {
    const prefix = theme.prefixes[level] || "";
    const color = theme.colors[level];
    const indent = groupMode === "text" ? calculateGroupIndent(depth) : "";
    const structuralMessage = buildStructuredLoggerMessage({
        indent,
        name,
        showName,
        prefix,
        message,
    });
    if (!color) {
        safeConsole(level, structuralMessage, ...optionalParams);
        return;
    }
    const [formatted, style] = formatWithColor(color, structuralMessage);
    safeConsole(level, formatted, style, ...optionalParams);
}
/**
 * Prepends logger name to message if enabled.
 * Pure function with no side effects.
 *
 * @param message - Original message
 * @param name - Logger name
 * @param show - Whether to show name
 * @returns Message with name prefix if enabled
 */
function applyName(message, name, show) {
    if (!show)
        return message;
    return `[${name}] ${message}`;
}
/**
 * Creates a new logger instance with enclosed state.
 * Uses closures to maintain private state without classes.
 *
 * @param name - Logger identifier
 * @returns Logger instance with independent configuration and state
 */
export function createLogger(name) {
    // Enclosed state (private to this logger instance)
    let level = "log";
    let theme = themes.default;
    let groupMode = "default";
    let showName = true;
    let groupStack = 0;
    const enterStack = [];
    /**
     * Tracks which handles actually produced a visual enter output.
     *
     * This is required because handles can be created while logging is disabled,
     * then later exited after logging is enabled. Only handles that incremented
     * groupStack should be allowed to decrement it.
     */
    const didLogEnter = new WeakSet();
    /**
     * Checks whether a handle produced a visual enter output.
     *
     * @param handle - Handle returned by enter()/enterStyled()
     * @returns True when the enter call emitted output and incremented groupStack
     */
    function wasEnteredVisually(handle) {
        return didLogEnter.has(handle);
    }
    /**
     * Removes all tracked handles from a given stack index onwards.
     *
     * @param startIndex - First index to remove (inclusive)
     */
    function deleteTrackedEntersFromIndex(startIndex) {
        for (let i = startIndex; i < enterStack.length; i++) {
            const h = enterStack[i];
            if (h) {
                didLogEnter.delete(h);
            }
        }
    }
    // Helper: logs if level permits
    const logAtLevel = (messageLevel, message, ...optionalParams) => {
        if (shouldLog(messageLevel, level)) {
            outputWithTheme(messageLevel, theme, groupMode, groupStack, message, name, showName, ...optionalParams);
        }
    };
    return {
        setLogLevel(newLevel) {
            level = newLevel;
        },
        setTheme(newTheme) {
            theme = newTheme;
        },
        setGroupMode(mode) {
            groupMode = mode;
        },
        setShowName(show) {
            showName = show;
        },
        fatal(message, ...optionalParams) {
            logAtLevel("fatal", message, ...optionalParams);
        },
        error(message, ...optionalParams) {
            logAtLevel("error", message, ...optionalParams);
        },
        warn(message, ...optionalParams) {
            logAtLevel("warn", message, ...optionalParams);
        },
        log(message, ...optionalParams) {
            logAtLevel("log", message, ...optionalParams);
        },
        info(message, ...optionalParams) {
            logAtLevel("info", message, ...optionalParams);
        },
        debug(message, ...optionalParams) {
            logAtLevel("debug", message, ...optionalParams);
        },
        verbose(message, ...optionalParams) {
            logAtLevel("verbose", message, ...optionalParams);
        },
        trace(message, ...optionalParams) {
            logAtLevel("trace", message, ...optionalParams);
        },
        group(label) {
            if (shouldLog("log", level)) {
                const labelWithName = applyName(label, name, showName);
                if (groupMode === "default") {
                    safeGroup(typeof labelWithName === "string" || labelWithName === undefined
                        ? labelWithName
                        : String(labelWithName));
                }
                else {
                    // Text mode grouping uses prefix
                    const indent = calculateGroupIndent(groupStack);
                    const prefix = `${indent}├─`;
                    safeConsole("log", `${prefix} ${labelWithName}`);
                }
                groupStack++;
            }
        },
        groupEnd() {
            if (shouldLog("log", level) && groupStack > 0) {
                groupStack--;
                if (groupMode === "default") {
                    safeGroupEnd();
                }
                // Text mode has no explicit end marker
            }
        },
        enter(label, ...optionalParams) {
            const entryLevel = "trace";
            const handle = {
                label,
                startTime: performance.now(),
                level: entryLevel,
                optionalParams: optionalParams.length > 0 ? optionalParams : undefined,
            };
            if (shouldLog(entryLevel, level)) {
                const enterPrefix = theme.prefixes.enter || "";
                const messageWithPrefix = buildEnterMessageWithPrefix(label, enterPrefix);
                const messageWithName = applyName(messageWithPrefix, name, showName);
                if (groupMode === "default") {
                    safeGroup(typeof messageWithName === "string" || messageWithName === undefined
                        ? messageWithName
                        : String(messageWithName), ...optionalParams);
                }
                else {
                    const indent = calculateGroupIndent(groupStack);
                    const textPrefix = `${indent}├─`;
                    safeConsole(entryLevel, `${textPrefix} ${messageWithName}`, ...optionalParams);
                }
                didLogEnter.add(handle);
                groupStack++;
            }
            enterStack.push(handle);
            return handle;
        },
        exit(handle, ...optionalParams) {
            const finalParams = optionalParams.length > 0
                ? optionalParams
                : handle.optionalParams || [];
            const index = enterStack.indexOf(handle);
            const unwindResult = unwindEnterStack(enterStack, index, groupStack, (h, currentGroupStack) => {
                if (!wasEnteredVisually(h)) {
                    didLogEnter.delete(h);
                    return currentGroupStack;
                }
                const elapsed = performance.now() - h.startTime;
                const elapsedMsg = calculateElapsedMessage(h.label, elapsed);
                const msg = buildExitMessageWithPrefix(elapsedMsg, theme.prefixes.exit || "");
                const msgWithName = applyName(msg, name, showName);
                warnIfSlow(elapsed, h.label);
                const newGroupStack = outputExitMessage(typeof msgWithName === "string" ? msgWithName : String(msgWithName), h.level, currentGroupStack, groupMode, ...finalParams);
                didLogEnter.delete(h);
                return newGroupStack;
            }, (lvl) => {
                return shouldLog(lvl, level);
            });
            if (unwindResult.shouldReturn) {
                return;
            }
            if (!unwindResult.isNormalExit) {
                // Unwinding path: update state and truncate stack
                deleteTrackedEntersFromIndex(unwindResult.newEnterStackLength);
                groupStack = unwindResult.newGroupStack;
                enterStack.length = unwindResult.newEnterStackLength;
            }
            else {
                // Normal exit path: pop and process
                enterStack.pop();
                if (wasEnteredVisually(handle) && shouldLog(handle.level, level)) {
                    const elapsed = performance.now() - handle.startTime;
                    const elapsedMsg = calculateElapsedMessage(handle.label, elapsed);
                    const msg = buildExitMessageWithPrefix(elapsedMsg, theme.prefixes.exit || "");
                    const msgWithName = applyName(msg, name, showName);
                    warnIfSlow(elapsed, handle.label);
                    groupStack = outputExitMessage(typeof msgWithName === "string" ? msgWithName : String(msgWithName), handle.level, groupStack, groupMode, ...finalParams);
                }
                didLogEnter.delete(handle);
            }
        },
        enterStyled(rawLabel, styledLabel, ...optionalParams) {
            const entryLevel = "trace";
            const handle = {
                label: styledLabel,
                rawLabel,
                startTime: performance.now(),
                level: entryLevel,
                optionalParams: optionalParams.length > 0 ? optionalParams : undefined,
            };
            if (shouldLog(entryLevel, level)) {
                const enterPrefix = theme.prefixes.enter || "";
                const messageWithPrefix = buildEnterMessageWithPrefix(styledLabel, enterPrefix);
                const messageWithName = applyName(messageWithPrefix, name, showName);
                if (groupMode === "default") {
                    safeGroup(typeof messageWithName === "string" || messageWithName === undefined
                        ? messageWithName
                        : String(messageWithName), ...optionalParams);
                }
                else {
                    const indent = calculateGroupIndent(groupStack);
                    const textPrefix = `${indent}├─`;
                    safeConsole(entryLevel, `${textPrefix} ${messageWithName}`, ...optionalParams);
                }
                didLogEnter.add(handle);
                groupStack++;
            }
            enterStack.push(handle);
            return handle;
        },
        exitStyled(handle, styledLabel, ...optionalParams) {
            const finalParams = optionalParams.length > 0
                ? optionalParams
                : handle.optionalParams || [];
            const index = enterStack.indexOf(handle);
            const unwindResult = unwindEnterStack(enterStack, index, groupStack, (h, currentGroupStack) => {
                if (!wasEnteredVisually(h)) {
                    didLogEnter.delete(h);
                    return currentGroupStack;
                }
                const elapsed = performance.now() - h.startTime;
                const elapsedMsg = calculateElapsedMessage(h.label, elapsed);
                const msg = buildExitMessageWithPrefix(elapsedMsg, theme.prefixes.exit || "");
                const msgWithName = applyName(msg, name, showName);
                warnIfSlow(elapsed, h.label);
                const newGroupStack = outputExitMessage(typeof msgWithName === "string" ? msgWithName : String(msgWithName), h.level, currentGroupStack, groupMode, ...finalParams);
                didLogEnter.delete(h);
                return newGroupStack;
            }, (lvl) => {
                return shouldLog(lvl, level);
            });
            if (unwindResult.shouldReturn) {
                return;
            }
            if (!unwindResult.isNormalExit) {
                // Unwinding path: update state and truncate stack
                deleteTrackedEntersFromIndex(unwindResult.newEnterStackLength);
                groupStack = unwindResult.newGroupStack;
                enterStack.length = unwindResult.newEnterStackLength;
            }
            else {
                // Normal exit path: pop and process
                enterStack.pop();
                if (wasEnteredVisually(handle) && shouldLog(handle.level, level)) {
                    const elapsed = performance.now() - handle.startTime;
                    const elapsedMsg = calculateElapsedMessage(styledLabel, elapsed);
                    const msgWithName = applyName(elapsedMsg, name, showName);
                    warnIfSlow(elapsed, handle.rawLabel);
                    groupStack = outputExitMessage(typeof msgWithName === "string" ? msgWithName : String(msgWithName), handle.level, groupStack, groupMode, ...finalParams);
                }
                didLogEnter.delete(handle);
            }
        },
    };
}
